import React, { useState, useEffect, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";

import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Space,
  Empty,
  Spin,
  Card,
  Typography,
  Tooltip,
  Badge,
  Switch,
  Tag,
} from "antd";
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  BankOutlined,
  SearchOutlined,
  MailOutlined,
  EnvironmentOutlined 
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  getAllSubOrganisations,
  getCurrentSubOrganisations,
  createSubOrganisation,
  updateSubOrganisation,
  deleteSubOrganisation,
  clearErrors
} from "../redux/slieces/subOrganisationSlice";
import SubOrganisationDTO from "../DTOs/subOrganisationDTO";
import styles from "./OrganisationManagement.module.css";
import { useKeycloak } from "@react-keycloak/web";

const { Title, Text } = Typography;

const OrganisationManagement = () => {
  // Translation hook - must be called before any early returns
  const { t } = useTranslation();
  
  // Redux state management
  const dispatch = useDispatch();
  const { subOrganisations, currentSubOrganisation, operations } = useSelector(
    (state) => state.subOrganisationData
  );
  
  // Keycloak context for authentication
  const { keycloak, initialized } = useKeycloak();
  
  // Check if user is authenticated and initialized
  if (!initialized) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" tip={t("loadingAuth")} />
      </div>
    );
  }
  
  // Check if user has admin role
  const isAdmin = keycloak.tokenParsed?.realm_access?.roles?.includes('mediadmin');
  
  // If not admin, show access denied
  if (!isAdmin) {
    return (
      <div className={styles.accessDenied}>
        <h2>{t("accessDenied")}</h2>
        <p>{t("adminAccessRequired")}</p>
        <p>{t("contactAdministrator")}</p>
      </div>
    );
  }
  
  // Local state for UI management
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentOrganisation, setCurrentOrganisation] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  
  // Extract data and loading states from Redux
  const organisations = subOrganisations.data || [];
  const loading = subOrganisations.loading || operations.creating || operations.updating || operations.deleting;
  const error = subOrganisations.error || operations.error;

  // Fetch sub-organisations on component mount
  useEffect(() => {
    dispatch(getAllSubOrganisations());
  }, [dispatch]);

  // Handle errors from Redux state
  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearErrors());
    }
  }, [error, dispatch]);

  // Filter organisations based on search term
  const filteredOrganisations = organisations.filter(
    (org) =>
      org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const showAddModal = () => {
    setIsAddModalVisible(true);
  };

  const handleAdd = () => {
    addForm
      .validateFields()
      .then(async (values) => {
        // Extract organization ID from Keycloak token using the robust approach
        const adminOrgId =
          keycloak.tokenParsed && keycloak.tokenParsed.organisationId
            ? Array.isArray(keycloak.tokenParsed.organisationId)
              ? keycloak.tokenParsed.organisationId[0]
              : keycloak.tokenParsed.organisationId
            : null;

        // Debug: Log token information
        console.log("🔍 DEBUG: Keycloak token parsed:", keycloak.tokenParsed);
        console.log("🔍 DEBUG: Extracted adminOrgId:", adminOrgId);
        console.log("🔍 DEBUG: Form values:", values);

        if (!adminOrgId) {
          return message.error("Organisation ID not found in token claims.");
        }

        // Create SubOrganisationDTO instance
        const newSubOrganisation = new SubOrganisationDTO();
        newSubOrganisation.organizationId = adminOrgId;

        newSubOrganisation.name = values.name;
        newSubOrganisation.address = values.address;
        newSubOrganisation.email = values.email;
        newSubOrganisation.phone = values.phone || '';
        newSubOrganisation.website = values.website || '';
        newSubOrganisation.description = values.description || '';
        newSubOrganisation.status = 'Active';
        newSubOrganisation.isDefault = false;

        // Debug: Log the final DTO before sending
        console.log("🔍 DEBUG: Final SubOrganisation DTO:", newSubOrganisation);
        console.log("🔍 DEBUG: DTO as API request:", newSubOrganisation.toApiRequest());

        try {
          await dispatch(createSubOrganisation(newSubOrganisation)).unwrap();
          message.success(t("organisationAdded"));
          addForm.resetFields();
          setIsAddModalVisible(false);
        } catch (error) {
          console.error("Error creating sub-organisation:", error);
          message.error(t("organisationAddError") || "Failed to add organisation");
        }
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const handleEdit = (record) => {
    setCurrentOrganisation(record);
    editForm.setFieldsValue({
      name: record.name,
      address: record.address,
      email: record.email,
      isDefault: record.isDefault || false,
    });
    setIsEditModalVisible(true);
  };

  const handleSaveEdit = () => {
    editForm
      .validateFields()
      .then(async (values) => {
        // Create updated SubOrganisationDTO instance
        const updatedSubOrganisation = new SubOrganisationDTO();
        updatedSubOrganisation.id = currentOrganisation.id;
        updatedSubOrganisation.organizationId = currentOrganisation.organizationId;
        updatedSubOrganisation.name = values.name;
        updatedSubOrganisation.address = values.address;
        updatedSubOrganisation.email = values.email;
        updatedSubOrganisation.phone = values.phone || currentOrganisation.phone || '';
        updatedSubOrganisation.website = values.website || currentOrganisation.website || '';
        updatedSubOrganisation.description = values.description || currentOrganisation.description || '';
        updatedSubOrganisation.status = currentOrganisation.status || 'Active';
        updatedSubOrganisation.isDefault = values.isDefault !== undefined ? values.isDefault : (currentOrganisation.isDefault || false);
        updatedSubOrganisation.logo = currentOrganisation.logo || '';

        try {
          await dispatch(updateSubOrganisation({ 
            id: currentOrganisation.id, 
            subOrganisationData: updatedSubOrganisation 
          })).unwrap();
          
          // Refresh the organisations list to ensure table shows updated data
          await dispatch(getAllSubOrganisations());
          
          message.success(t("organisationUpdated"));
          setIsEditModalVisible(false);
          setCurrentOrganisation(null);
        } catch (error) {
          console.error("Error updating sub-organisation:", error);
          message.error(t("organisationUpdateError") || "Failed to update organisation");
        }
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteSubOrganisation(id)).unwrap();
      message.success(t("organisationDeleted"));
    } catch (error) {
      console.error("Error deleting sub-organisation:", error);
      message.error(t("organisationDeleteError") || "Failed to delete organisation");
    }
  };

  const handleCancelAdd = () => {
    addForm.resetFields();
    setIsAddModalVisible(false);
  };

  const handleCancelEdit = () => {
    setIsEditModalVisible(false);
    setCurrentOrganisation(null);
  };

  const columns = [
    {
      title: t("organisationName"),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: t("address"),
      dataIndex: "address",
      key: "address",
    },
    {
      title: t("email"),
      dataIndex: "email",
      key: "email",
    },
    {
      title: t("isDefault"),
      dataIndex: "isDefault",
      key: "isDefault",
      render: (isDefault) => (
        <Tag color={isDefault ? "green" : "default"}>
          {isDefault ? t("default") : t("notDefault")}
        </Tag>
      ),
      sorter: (a, b) => (a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1),
      filters: [
        { text: t("default"), value: true },
        { text: t("notDefault"), value: false },
      ],
      onFilter: (value, record) => record.isDefault === value,
    },
    {
      title: t("action"),
      key: "action",
      render: (text, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t("edit")}
          </Button>
          <Popconfirm
            title={t("confirmDeleteOrganisation")}
            onConfirm={() => handleDelete(record.id)}
            okText={t("yes")}
            cancelText={t("no")}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              {t("delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Enhanced empty state component
  const EmptyState = () => (
    <Empty
      image={<BankOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
      imageStyle={{ height: 80 }}
      description={
        <div>
          <Text type="secondary" style={{ fontSize: 16 }}>
            {t("noOrganisationsFound")}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 14 }}>
            {t("addFirstOrganisation")}
          </Text>
        </div>
      }
    >
      <Button 
        type="primary" 
        icon={<PlusOutlined />} 
        onClick={showAddModal}
        size="large"
      >
        {t("addOrganisation")}
      </Button>
    </Empty>
  );

  return (
    <div className={styles.organisationManagement}>
      {/* Enhanced Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.titleSection}>
            <Title level={3} className={styles.sectionTitle}>
              <BankOutlined className={styles.sectionIcon} />
              {t("organisationManagement")}
            </Title>
            <Text type="secondary" className={styles.sectionDescription}>
              {t("organisationManagementDescription")}
            </Text>
          </div>
          <div className={styles.statsSection}>
            <Badge 
              count={organisations.length} 
              showZero 
              color="#1890ff"
              style={{ backgroundColor: '#1890ff' }}
            >
              <Card size="small" className={styles.statCard}>
                <Text strong>{t("totalOrganisations")}</Text>
              </Card>
            </Badge>
          </div>
        </div>
      </div>

      {/* Enhanced Controls */}
      <div className={styles.controls}>
        <div className={styles.leftControls}>
          <Tooltip title={t("addNewOrganisation")}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showAddModal}
              className={styles.addButton}
              size="large"
            >
              {t("addOrganisation")}
            </Button>
          </Tooltip>
        </div>
        <div className={styles.rightControls}>
          <Input.Search
            placeholder={t("searchOrganisation")}
            allowClear
            prefix={<SearchOutlined />}
            onSearch={(value) => setSearchTerm(value)}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 320 }}
            size="large"
          />
        </div>
      </div>

      {/* Enhanced Table Container */}
      <div className={styles.tableContainer}>
        <Spin spinning={loading} tip={t("loadingOrganisations")}>
          {filteredOrganisations.length === 0 && !loading ? (
            <EmptyState />
          ) : (
            <Table
              dataSource={filteredOrganisations}
              columns={columns}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} ${t("of")} ${total} ${t("organisations")}`,
              }}
              scroll={{ x: 800 }}
              className={styles.enhancedTable}
            />
          )}
        </Spin>
      </div>

      {/* Add Organisation Modal */}
      <Modal
        title={t("addOrganisation")}
        open={isAddModalVisible}
        onOk={handleAdd}
        onCancel={handleCancelAdd}
        okText={t("add")}
        cancelText={t("cancel")}
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            name="name"
            label={t("organisationName")}
            rules={[
              { required: true, message: t("organisationNameRequired") },
              { min: 2, message: t("organisationNameMinLength") },
            ]}
          >
            <Input placeholder={t("organisationNamePlaceholder")} />
          </Form.Item>
          <Form.Item
            name="address"
            label={t("address")}
            rules={[
              { required: true, message: t("addressRequired") },
              { min: 5, message: t("addressMinLength") },
            ]}
          >
            <Input.TextArea
              rows={3}
              placeholder={t("addressPlaceholder")}
            />
          </Form.Item>
          <Form.Item
            name="email"
            label={t("email")}
            rules={[
              { required: true, message: t("emailRequired") },
              { type: "email", message: t("emailInvalid") },
            ]}
          >
            <Input placeholder={t("emailPlaceholder")} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Organisation Modal */}
      <Modal
        title={t("editOrganisation")}
        open={isEditModalVisible}
        onOk={handleSaveEdit}
        onCancel={handleCancelEdit}
        okText={t("saveChanges")}
        cancelText={t("cancel")}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="name"
            label={t("organisationName")}
            rules={[
              { required: true, message: t("organisationNameRequired") },
              { min: 2, message: t("organisationNameMinLength") },
            ]}
          >
            <Input placeholder={t("organisationNamePlaceholder")} />
          </Form.Item>
          <Form.Item
            name="address"
            label={t("address")}
            rules={[
              { required: true, message: t("addressRequired") },
              { min: 5, message: t("addressMinLength") },
            ]}
          >
            <Input.TextArea
              rows={3}
              placeholder={t("addressPlaceholder")}
            />
          </Form.Item>
          <Form.Item
            name="email"
            label={t("email")}
            rules={[
              { required: true, message: t("emailRequired") },
              { type: "email", message: t("emailInvalid") },
            ]}
          >
            <Input placeholder={t("emailPlaceholder")} />
          </Form.Item>
          <Form.Item
            name="isDefault"
            label={t("setAsDefault")}
            valuePropName="checked"
            tooltip={t("defaultOrganisationTooltip")}
          >
            <Switch 
              checkedChildren={t("default")} 
              unCheckedChildren={t("notDefault")}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OrganisationManagement;
